/**
 * Shared "Save changes" / "No changes yet" bar for the new-UX ParticipantDialog panels
 * (Supporting Actors and Dates, Timeline Item Details). Presentational + a thin save
 * wrapper — the dirty check itself (isEqual(d, originalD)) and the actual `saveInstance`
 * call live in the parent that owns `d`/`originalD`.
 */
import styled from '@emotion/styled';
import React, { useState } from 'react';
import { NewUxButton } from '../../../components/Builders/newux/kit/Button';
import { tokens } from '../../../components/Builders/newux/kit/tokens';
import { useUnsafeTranslation } from '../../../../../i18next';

const { color, font } = tokens;

const Bar = styled('div')`
  flex: 0 0 auto;
  position: sticky;
  bottom: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${tokens.space(3)};
  padding: ${tokens.space(3)} ${tokens.space(5)};
  border-top: 1px solid ${color.borderSubtle};
  background: ${color.white};
`;

const Status = styled('div')`
  font-size: ${font.variant.caption.fontSize}px;
  color: ${color.textSecondary};
`;

const Actions = styled('div')`
  display: flex;
  align-items: center;
  gap: ${tokens.space(2)};
`;

export const SaveDiscardBar: React.FC<{
  isDirty: boolean;
  onSave: () => Promise<void>;
  onDiscard: () => void;
}> = ({ isDirty, onSave, onDiscard }) => {
  const { t } = useUnsafeTranslation();
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Bar>
      <Status>{isDirty ? t('Unsaved changes') : t('No changes yet')}</Status>
      <Actions>
        <NewUxButton
          variant="outlined"
          color="neutral"
          disabled={!isDirty || saving}
          onClick={onDiscard}
          style={{ padding: '10px 14px' }}
        >
          {t('Discard')}
        </NewUxButton>
        <NewUxButton
          variant="contained"
          color="primary"
          disabled={!isDirty || saving}
          onClick={handleSave}
        >
          {saving ? t('Saving...') : t('Save changes')}
        </NewUxButton>
      </Actions>
    </Bar>
  );
};
